import { ApiError } from '../api/client'
import { api } from '../api/endpoints'
import type { DeviceInfo, Me } from '../api/types'
import { UNBOUND_DEVICE } from '../state/device'
import { attestationMessage, detectPlatform, getOrCreateDeviceKey, isInstalled, publicJwk, sign } from './deviceKey'

export const INVALID_ATTESTATION = 'invalid_attestation'

export function isInvalidAttestation(error: unknown): boolean {
  return error instanceof ApiError && error.status === 400 && error.error === INVALID_ATTESTATION
}

export interface AttestOptions {
  /** Attest even when the session is already bound to this key (e.g. "Request again" for an expired request). */
  force?: boolean
}

/** Challenge → sign → attest with the local key. Retries once with a fresh challenge on invalid_attestation. */
export async function attestDevice(): Promise<DeviceInfo> {
  const { keyPair, deviceId } = await getOrCreateDeviceKey()
  const publicKey = await publicJwk(keyPair.publicKey)
  for (let attempt = 0; ; attempt++) {
    const { nonce } = await api.deviceChallenge()
    const signature = await sign(attestationMessage(nonce), keyPair.privateKey)
    try {
      return await api.attestDevice({
        deviceId,
        publicKey,
        signature,
        platform: detectPlatform(),
        installed: isInstalled(),
      })
    } catch (error) {
      // The nonce may have expired or been consumed by another tab: one fresh attempt.
      if (attempt === 0 && isInvalidAttestation(error)) continue
      throw error
    }
  }
}

/**
 * Attestation flow from docs/api-contract.md, run after GET /api/me succeeds:
 * - policy "off": nothing to do;
 * - the session is bound to this installation's key: nothing to do;
 * - unbound, or bound to a different key (site data was cleared while the cookie survived): attest.
 */
export async function ensureDeviceAttested(me: Me, options: AttestOptions = {}): Promise<DeviceInfo> {
  const current = me.device ?? UNBOUND_DEVICE
  if ((me.devicePolicy ?? 'off') === 'off') return current
  const { deviceId } = await getOrCreateDeviceKey()
  if (!options.force && current.status !== 'unbound' && current.id === deviceId) return current
  return attestDevice()
}
